import * as React from 'react'
import ChatbotWrapper from '../../components/ChatbotWrapper'
import styles from './styles.module.scss'

const LAST_UPDATED = 'August 23, 2026'

const Terms: React.FC = () => {
  return (
    <ChatbotWrapper
      decorate
      title='Terms of Service'
      header={
        <>
          <p className='display-6'>
            These Terms explain the rules for using Draco.Bot and draconem.dev. The short version: use Draco.Bot responsibly, understand that third-party integrations can change or break, and do not
            rely on the software as though it is guaranteed to work forever.
          </p>
          <p className={styles.updated}>Last updated: {LAST_UPDATED}</p>
        </>
      }
    >
      <div className={styles.terms}>
        <section>
          <h2>Agreement to these Terms</h2>
          <p>
            These Terms of Service apply when you download, install, or use <strong>Draco.Bot</strong>, or when you use the <strong>draconem.dev</strong> website and related services operated by
            Draconem Labs.
          </p>
          <p>If you do not agree to these Terms, do not use Draco.Bot or the related Draconem Labs services.</p>
        </section>

        <section>
          <h2>What Draco.Bot is</h2>
          <p>
            Draco.Bot is an application for stream automation and related integrations. It can connect to software and third-party services you choose to use, including services such as Twitch and
            Discord and local applications such as OBS Studio and VTube Studio.
          </p>
          <p>Draco.Bot is provided as a tool. Draconem Labs does not operate your stream, moderate your communities, or control the actions and automation you configure.</p>
        </section>

        <section>
          <h2>Your responsibility for automation</h2>
          <p>You are responsible for the commands, actions, event responses, messages, media, integrations, permissions, and other automation you configure in Draco.Bot.</p>
          <p>
            Draco.Bot may perform actions quickly and automatically when configured conditions are met. You are responsible for reviewing and testing your configuration and for actions performed
            through accounts, applications, or third-party services that you connect to Draco.Bot.
          </p>
        </section>

        <section>
          <h2>Third-party services</h2>
          <p>
            Draco.Bot and draconem.dev may interact with third-party products and services. Your use of those products and services remains subject to their own terms, policies, permissions, limits,
            and technical requirements.
          </p>
          <p>
            Draconem Labs is not affiliated with or responsible for third-party services merely because Draco.Bot can interact with them. Third-party APIs, desktop clients, features, authentication
            systems, or other interfaces may change, become unavailable, or stop working with Draco.Bot at any time.
          </p>
        </section>

        <section>
          <h2>Acceptable use</h2>
          <p>You may not use Draco.Bot or draconem.dev to:</p>
          <ul>
            <li>Violate applicable law or the rights of another person.</li>
            <li>Harass, threaten, defraud, or deliberately harm others.</li>
            <li>Gain unauthorized access to accounts, systems, services, or data.</li>
            <li>Bypass or defeat restrictions, security measures, or access controls imposed by third-party services.</li>
            <li>Disrupt, overload, damage, or interfere with Draconem Labs services or third-party services.</li>
            <li>Distribute malware or use Draco.Bot as part of malicious or abusive activity.</li>
          </ul>
        </section>

        <section>
          <h2>Availability, updates, and beta software</h2>
          <p>Draco.Bot is actively developed and may include beta, preview, or release-candidate functionality. Features may contain bugs, behave unexpectedly, change, or be removed.</p>
          <p>
            We may update, replace, suspend, or discontinue Draco.Bot, draconem.dev, or individual features at any time. We do not guarantee that any particular integration or feature will remain
            available or compatible with a third-party service.
          </p>
        </section>

        <section>
          <h2>Intellectual property and third-party names</h2>
          <p>
            Draco.Bot, the Draconem Labs name and branding, and original Draconem Labs website content remain the property of their respective owners. These Terms do not transfer ownership of
            Draco.Bot or Draconem Labs intellectual property to you.
          </p>
          <p>
            All third-party trademarks, logos, and brand names are the property of their respective owners. Company, product, and service names used by Draco.Bot or draconem.dev are for identification
            purposes only. Their use does not imply endorsement, sponsorship, or affiliation.
          </p>
        </section>

        <section>
          <h2>No warranties</h2>
          <p>
            Draco.Bot and draconem.dev are provided on an <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> basis. You use them at your own risk.
          </p>
          <p className={styles.disclaimer}>
            THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE AND NONINFRINGEMENT.
          </p>
        </section>

        <section>
          <h2>Limitation of liability</h2>
          <p className={styles.disclaimer}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL DRACONEM LABS, ITS AUTHORS, CONTRIBUTORS, OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES, OR OTHER LIABILITY, WHETHER IN AN
            ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF, OR IN CONNECTION WITH DRACO.BOT, DRACONEM.DEV, OR THEIR USE OR OTHER DEALINGS.
          </p>
          <p>Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited under applicable law.</p>
        </section>

        <section>
          <h2>Privacy</h2>
          <p>
            Our <a href='/privacy'>Privacy Policy</a> explains how Draco.Bot and draconem.dev process information and is incorporated into these Terms by reference.
          </p>
        </section>

        <section>
          <h2>Changes to these Terms</h2>
          <p>We may update these Terms when Draco.Bot, draconem.dev, or the services they use change. The date at the top of this page will be updated when material changes are made.</p>
          <p>Your continued use of Draco.Bot or draconem.dev after updated Terms take effect constitutes acceptance of the updated Terms.</p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions about these Terms can be sent through the{' '}
            <a href='https://discord.gg/5s2rhp8qBq' target='_blank' rel='noreferrer'>
              Draconem Labs Discord community
            </a>
            .
          </p>
        </section>
      </div>
    </ChatbotWrapper>
  )
}

export default Terms
